import { DeleteIcon, EditIcon } from "@chakra-ui/icons";
import {
  Box,
  Center,
  Flex,
  IconButton,
  Image,
  Switch,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import api from "api/api";
import { openModalConfirm } from "components/modal-confirm-new";
import { SkeletonLoading } from "components/skeleton-loading";
import { LogoutIcon2 } from "components/vectors/logout-icon";
import { RepeatIcon } from "components/vectors/repeat-icon";
import { WhatsAppIcon } from "components/vectors/whatsapp-icons";
import { Dispatch, SetStateAction, memo, useEffect, useState } from "react";

import { phoneFormat } from "utils/string-formats";

import { Toast } from "components/toast";
import type {
  WhatsAppSessionData,
  WhatsAppSessionListProps,
} from "./grid-servers/types";
import { openModalAddWhatsAppServer } from "./grid-servers/add-whatsapp-server-modal";
import { bgSucess, StatusFormat } from "components/dynamic-table/row-status";
import { UseWebSocketProps } from "components/atendimentos-components/use-init-web-socket/types";
import apiCarteira from "api/api-carteira";
import { useApplicationContext } from "contexts/ApplicationContext";

export type PortalPages = "qrcode-generated" | "chat" | "qrcode-not-generated";

function WhatsAppPortal({
  id,
  nome,
  whatsappSessionId,
  setServers,
  sendJsonMessage,
  type,
  reconnectWs,
  sessionsData,
  serviceData,
  ativoVendeAI,
  customerId,
}: {
  setServers: Dispatch<SetStateAction<WhatsAppSessionListProps[]>>;
  sendJsonMessage: UseWebSocketProps["sendJsonMessage"];
  reconnectWs: UseWebSocketProps["reconnect"];
  sessionsData: Map<string, WhatsAppSessionData>;
  serviceData: any;
} & WhatsAppSessionListProps) {
  const { user } = useApplicationContext();
  const {
    portalPage = "chat",
    qrCode,
    whatsAppNumber,
  } = sessionsData.get(whatsappSessionId) || {};
  const vendeAI = serviceData?.vendeAI?.ativo;

  const [loading, setLoading] = useState(true);
  const [isGeneratingQrCode, setIsGeneratingQrCode] = useState(false);
  useEffect(() => {
    if (sessionsData.get(whatsappSessionId)) {
      setLoading(false);
    }
  }, [portalPage, qrCode, whatsAppNumber]);

  const handleDelete = () => {
    openModalConfirm({
      message: `Deseja deletar o servidor: "${nome}"?`,
      onConfirm: async () => {
        try {
          const { status } = await api.delete(`/whatsapp-sessions/${id}`);
          Toast({
            title: "Sessão Deletada!",
            status: "info",
          });
          sendJsonMessage({ action: "update-pm2" });
          setServers((servers) => servers.filter((server) => server.id !== id));
          reconnectWs();
        } catch (e) {
          Toast({
            title: "Erro ao deletar sessão!",
            status: "error",
          });
        }
      },
      confirmButtonStyle: { loadingText: "Deletando" },
    });
  };

  const handleDisconnect = () => {
    openModalConfirm({
      message: `Deseja desconectar o servidor: "${nome}"?`,
      onConfirm: async () => {
        sendJsonMessage({ action: "disconnect", whatsappSessionId });
      },
      confirmButtonStyle: { loadingText: "Desconectando" },
    });
  };

  const pages = {
    "qrcode-not-generated": (
      <Flex
        onClick={() => {
          if (!isGeneratingQrCode) {
            sendJsonMessage({
              action: "generate-qrcode",
              whatsappSessionId,
            });
            setIsGeneratingQrCode(true);
          }
        }}
        w="200px"
        sx={{ aspectRatio: "1" }}
        bg="#60AFA1"
        _hover={{ bg: "#7EBEB2" }}
        transition="0.2s"
        borderRadius="full"
        px="15px"
        alignItems="center"
        justifyContent="center"
        flexDir="column"
        textAlign="center"
        color="white"
        cursor={isGeneratingQrCode ? undefined : "pointer"}
      >
        <RepeatIcon
          display="block"
          mb="25px"
          isAnimating={isGeneratingQrCode}
        />
        <Text maxW="200px" color="white" fontWeight="400" fontSize="12">
          {isGeneratingQrCode
            ? "RECARREGANDO O CÓDIGO QR"
            : "CLIQUE PARA RECARREGAR O CÓDIGO QR"}
        </Text>
      </Flex>
    ),
    "qrcode-generated": (
      <>
        <Image src={qrCode} h="calc(100% - 20px)" />
      </>
    ),
    chat: (
      <Box fontSize="18" textAlign="center">
        <Text textAlign="center">
          <Text as="span">
            {whatsAppNumber?.startsWith("55") &&
            (whatsAppNumber.length === 13 || whatsAppNumber.length === 12)
              ? phoneFormat(whatsAppNumber, {
                  withCountryNumber: whatsAppNumber.length >= 12,
                })
              : whatsAppNumber}
          </Text>{" "}
        </Text>{" "}
        <Box
          w="100%"
          p="2px 10px"
          borderRadius="4"
          bg="green.500"
          fontWeight="semibold"
          color="#fff"
          onClick={() => {}}
          mb="8px"
        >
          Conectado
        </Box>
      </Box>
    ),
  };

  const parsedType = {
    ATENDIMENTO: "Atendimento",
    DISPARO_CAMPANHA: "Disparo de Campanha",
  }[type];

  return (
    <Box>
      <Flex alignItems="center" mb="8px">
        <WhatsAppIcon display="block" mr="10px" /> <Text>{nome}</Text>
      </Flex>
      <SkeletonLoading isLoading={loading} borderRadius="10px">
        <Center
          p="8px"
          h="250px"
          flexDir="column"
          css={{
            backgroundImage: `${
              portalPage === "chat"
                ? "url(/assets/backgrounds/whatsapp.svg),"
                : ""
            } linear-gradient(#fff, #fff)`,
          }}
          bgSize={portalPage === "chat" ? "40% 100%, 100% 100%" : "100% 100%"}
          bgRepeat="no-repeat"
          bgPos="center"
          borderRadius="10px"
          pos="relative"
          border="1px solid var(--chakra-colors-gray-200)"
        >
          <Flex pos="absolute" right="8px" top="8px">
            {portalPage === "chat" ? (
              loading ? null : (
                <Tooltip label="Deslogar">
                  <IconButton
                    aria-label=""
                    size="sm"
                    icon={<LogoutIcon2 />}
                    onClick={handleDisconnect}
                    mr="8px"
                  />
                </Tooltip>
              )
            ) : null}
            <Tooltip label="Editar">
              <IconButton
                aria-label=""
                size="sm"
                icon={<EditIcon />}
                onClick={() => openModalAddWhatsAppServer({ serverId: id })}
                mr="8px"
              />
            </Tooltip>
            <Tooltip label="Excluir">
              <IconButton
                aria-label=""
                size="sm"
                icon={<DeleteIcon />}
                variant="danger"
                pos="relative"
                zIndex="100"
                onClick={handleDelete}
              />
            </Tooltip>
          </Flex>
          {loading ? null : pages[portalPage]}
          <Flex
            justifyContent="space-between"
            alignItems="center"
            pos="absolute"
            bottom="8px"
            left="8px"
            right="8px"
          >
            <Flex>
              <StatusFormat
                width="100%"
                bg={
                  type === "DISPARO_CAMPANHA"
                    ? bgSucess
                    : "rgba(98, 171, 250, 0.2)"
                }
              >
                {parsedType}
              </StatusFormat>
            </Flex>
            {vendeAI &&
            user.userData.customerId === customerId &&
            ["SUPER", "COMERCIAL", "PARCEIRO_MASTER"].includes(
              user.userData.type!
            ) ? (
              <Flex alignItems="center">
                <Switch
                  onChange={async (e) => {
                    const value = e.target.checked;
                    setLoading(true);
                    try {
                      const { data } = await api.patch(
                        `/whatsapp-sessions/${id}/ativo-vendeai`,
                        { ativoVendeAI: value }
                      );
                      setServers((servers) =>
                        servers.map((server) => {
                          if (server.id === id) return data;
                          return server;
                        })
                      );
                    } catch (e: any) {
                      const errorType = e?.response?.data?.message;

                      if (
                        errorType === "equipe_associada_sessao" ||
                        errorType === "usuarios_associados_sessao"
                      ) {
                        const desc =
                          errorType === "equipe_associada_sessao"
                            ? "Essa sessão possui uma equipe associada. Deseja remover a associação e ativar a IA?"
                            : "Essa sessão possui usuário(s) associado(s). Deseja remover a associação e ativar a IA?";
                        openModalConfirm({
                          message: desc,
                          onConfirm: async () => {
                            setLoading(true);
                            try {
                              const { data } = await api.patch(
                                `/whatsapp-sessions/${id}/ativo-vendeai`,
                                { ativoVendeAI: value },
                                { params: { force: true } }
                              );
                              setServers((servers) =>
                                servers.map((server) => {
                                  if (server.id === id) return data;
                                  return server;
                                })
                              );
                            } catch (e) {
                              Toast({
                                title: `Erro ao ${
                                  value ? "ativar" : "desativar"
                                } VendeAI`,
                              });
                            } finally {
                              setLoading(false);
                            }
                          },
                        });
                      } else {
                        Toast({
                          title: `Erro ao ${
                            value ? "ativar" : "desativar"
                          } VendeAI`,
                        });
                      }
                    } finally {
                      setLoading(false);
                    }
                  }}
                  isChecked={!!ativoVendeAI}
                  colorScheme="green"
                ></Switch>
                <Text ml="8px">Ativar IA VendeAI</Text>
              </Flex>
            ) : null}
          </Flex>
        </Center>
      </SkeletonLoading>
    </Box>
  );
}

export default memo(WhatsAppPortal);
